import { ResultTypeFrom } from '@reduxjs/toolkit/query';
import { LegacyDataProviderRowData } from '@adv-frontend/data-provider';
import { useCallback, useMemo } from 'react';
import { mapSubRows } from './lib/mapSubRows/mapSubRows';
import { BaseRowData, RequestedChunks, UseEndpointDataProviderRowsGenerator } from './types';
import { AnyQueryDefinition, UseQueryStateDefaultResult } from '../apiSSRModule';
import { iterateOverChunks } from './lib/iterateOverChunks/iterateOverChunks';
import { useLoadedRows } from './lib/useLoadedRows/useLoadedRows';

/**
 * Параметры конфигурации динамического генератора строк датапровайдера.
 */
type DynamicRowsGeneratorConfig<Definition extends AnyQueryDefinition> = {
    /** Функция получения количества строк из ответа эндпоинта */
    getRowsCount(chunkData: ResultTypeFrom<Definition>): number;
};

/**
 * Хелпер получает последний запрошенный чанк и данные из кеша.
 *
 * @example
 * ```ts
 * const { lastRequestedChunk, lastCachedData } = getLastChunk([chunk1, chunk3, chunk2], [data1, data3, data2]);
 * // lastRequestedChunk = chunk3, lastCachedData = data3
 * ```
 * @param requestedChunks
 * @param cachedValues
 */
function getLastChunk<Definition extends AnyQueryDefinition>(
    requestedChunks: RequestedChunks<Definition>,
    cachedValues: UseQueryStateDefaultResult<Definition>[]
): {
    lastRequestedChunk: RequestedChunks<Definition>[number] | null;
    lastCachedData: ResultTypeFrom<Definition> | null;
} {
    let lastRequestedChunk = null;
    let lastCachedData = null;

    for (let i = 0; i < requestedChunks.length; i++) {
        const requestedChunk = requestedChunks[i];

        if (!requestedChunk) {
            continue;
        }

        if (!lastRequestedChunk || requestedChunk.startIndex > lastRequestedChunk.startIndex) {
            lastRequestedChunk = requestedChunk;
            lastCachedData = cachedValues[i]?.data ?? null;
        }
    }

    return { lastRequestedChunk, lastCachedData };
}

function calculateTotalRows<Definition extends AnyQueryDefinition>(
    requestedChunk: RequestedChunks<Definition>[number] | null,
    cachedData: ResultTypeFrom<Definition> | null,
    getRowsCount: (chunkData: ResultTypeFrom<Definition>) => number,
    chunkLength: number,
    hasReachedEnd: boolean
): number {
    // Если нет запрошенного чанка, то возвращаем длину одного чанка, чтобы отрисовать стабы.
    if (!requestedChunk) {
        return chunkLength;
    }

    // Иначе берем индекс переданного чанка (количество строк в предыдущих чанках) и добавляем к нему текущую длину данных в кеше.
    // Таким образом, получаем общее количество строк в провайдере (строки до startIndex + строки после в текущем чанке).
    // Также учитываем, что если данные в кеше еще не загружены, то добавляем дефолтную длину чанка.
    return requestedChunk.startIndex + (cachedData && hasReachedEnd ? getRowsCount(cachedData) : chunkLength);
}

/**
 * Создает динамический генератор строк датапровайдера.
 *
 * @param config Параметры конфигурации генератора строк
 * @returns Генератор строк датапровайдера
 */
export function createDynamicRowsGenerator<
    Definition extends AnyQueryDefinition,
    RowData extends BaseRowData,
    Params extends object,
>({
    getRowsCount: getRowsCountOptions,
}: DynamicRowsGeneratorConfig<Definition>): UseEndpointDataProviderRowsGenerator<Definition, RowData, Params> {
    return function useRowsGenerator(paginator, { getRowByIndex, cachedValues, requestedChunks }) {
        const chunkLength = paginator.getLimit();
        const hasMoreMethod = paginator?.hasMore;

        const hasMore = useCallback(
            (chunkData: ResultTypeFrom<Definition>) => {
                if (hasMoreMethod) {
                    return hasMoreMethod(chunkData);
                }

                return getRowsCountOptions(chunkData) >= chunkLength;
            },
            [hasMoreMethod, chunkLength]
        );

        const getRows = useCallback(() => {
            // Находим чанк последний по индексу в списке данных, а не по порядку загрузки чанков.
            // Пример: если загружены чанки [0, 1, 2, 9, 3, 4], то последним по индексу будет 9.
            const { lastRequestedChunk, lastCachedData } = getLastChunk(requestedChunks, cachedValues);

            // Проверяем условие окончания данных: если последний чанк не заполнен полностью, то данные закончились,
            // иначе добавляем еще один чанк для отображения стабов.
            // Если чанка нет, то это значит, что никаких строк еще запрошено не было, поэтому выставляем флаг в true, чтобы
            // отобразить стабы, количество которых равно длине чанка.
            const hasReachedEnd = lastCachedData ? !hasMore(lastCachedData) : true;

            // Вычисляем потенциальное общее количество строк в провайдере.
            const currentRowsLength = calculateTotalRows(
                lastRequestedChunk,
                lastCachedData,
                getRowsCountOptions,
                chunkLength,
                hasReachedEnd
            );
            // Если данные не закончились, то добавляем еще один чанк для отображения стабов.
            const totalRowsLength = hasReachedEnd ? currentRowsLength : currentRowsLength + chunkLength;

            /**
             * Формируем список всех строк в провайдере.
             * Строка может быть в одном из трех состояний:
             * 1. Строка загружена – есть id, status = 'loaded'
             * 2. Строка не загружена – вместо id `not_loaded_{index}`, status = 'not-loaded'
             * 3. Строка загружена, но данные для нее не получены – вместо id `load-failed_{index}`, status = 'load-failed'
             */

            const result: LegacyDataProviderRowData[] = Array(totalRowsLength)
                .fill(null)
                .map((_, index) => ({ id: `not_loaded_${index}`, status: 'not-loaded' }));

            iterateOverChunks(
                requestedChunks,
                cachedValues,
                (rowData, requestedChunk, rowIndexInChunk) => {
                    result[requestedChunk.startIndex + rowIndexInChunk] = {
                        id: rowData.id,
                        status: 'loaded',
                        ...(rowData?.subRows && { subRows: mapSubRows(rowData.subRows) }),
                    };
                },
                {
                    chunkLength,
                    getRowByIndex,
                },
                (requestedChunk, rowIndexInChunk, cachedValue) => {
                    /**
                     * Проверяем условие окончания данных по текущему чанку
                     * Если после текущего чанка еще есть данные, то мы ожидаем что в текущем чанке будут данные для всеx строк
                     * Если для какой-то строки данных нет, это значит нам не удалось их загрузить,
                     * поэтому помечаем такую строку как ошибочно загруженную
                     *
                     * Работает только при наличие метода `hasMore` у пагинатора
                     */
                    if (cachedValue?.data && hasMoreMethod?.(cachedValue.data)) {
                        result[requestedChunk.startIndex + rowIndexInChunk] = {
                            id: `load-failed_${requestedChunk.startIndex + rowIndexInChunk}`,
                            status: 'load-failed',
                        };
                    }
                }
            );

            return result;
        }, [requestedChunks, cachedValues, hasMore, chunkLength, getRowByIndex, hasMoreMethod]);

        const getLoadedRows = useLoadedRows(requestedChunks, cachedValues, getRowByIndex, chunkLength);

        const getTotalCount = useCallback(() => getRows().length, [getRows]);

        return useMemo(() => ({ getRows, getLoadedRows, getTotalCount }), [getRows, getLoadedRows, getTotalCount]);
    };
}
